// paex-store-page.js — Store details page behavior (ONE script for the whole
// template, deferred): hero open-now pill (America/Toronto via the shared
// core.js engine, holiday_hours override), hours card expand + row
// formatting, static→interactive map swap on tap, sticky mobile bar
// (IntersectionObserver), nearby-store haversine sort, and GA4 pushes
// (get_directions, call_store, hours_expand, event_reserve,
// nearby_store_click) gated on the theme analytics setting.
// Depends on paex-store-core.js (loaded first, both defer).
// Re-execution safe: define guards, several sections may emit the tag.

var PAEX_STORE_TZ = 'America/Toronto';

// Locale-correct time label from minutes-since-midnight: EN "9 PM" / FR
// "21 h" — both from the same stored number, never a hardcoded AM.
// forceMinutes always keeps the minutes ("9:00 PM") — the design uses that
// form for the pill's closing time and the event band's times.
function paexStorePageFormatMinutes(minutes, forceMinutes) {
  if (minutes == null) return '';
  var probe = new Date();
  probe.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  var lang = document.documentElement.lang || 'en';
  var options = minutes % 60 || forceMinutes ? { hour: 'numeric', minute: '2-digit' } : { hour: 'numeric' };
  try {
    // W3.7: Intl pads the FR hour ("09 h") — approved wording is "9 h".
    // EN never pads with hour: 'numeric', so the strip is FR-only in effect.
    return new Intl.DateTimeFormat(lang, options).format(probe).replace(/^0(\d)/, '$1');
  } catch (error) {
    return String(Math.floor(minutes / 60)) + (minutes % 60 || forceMinutes ? ':' + ('0' + (minutes % 60)).slice(-2) : '');
  }
}

// W3.7: FR ranges read "9 h à 17 h" (not the EN dash) — one joiner so every
// consumer (hours card, today line, event band) inherits the same wording.
function paexStorePageRangeJoiner() {
  var lang = String(document.documentElement.lang || 'en').toLowerCase();
  return lang.indexOf('fr') === 0 ? ' à ' : ' – ';
}

function paexStorePageRange(openMinutes, closeMinutes) {
  return paexStorePageFormatMinutes(openMinutes) + paexStorePageRangeJoiner() + paexStorePageFormatMinutes(closeMinutes);
}

function paexStorePagePush(enabled, eventName, handle) {
  if (!enabled) return;
  if (window.Shopify && window.Shopify.designMode) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, store_handle: handle || '' });
}

// Shared [data-ga] click delegation for a host element.
function paexStorePageBindGa(host, enabled, handle, signal) {
  host.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target.closest('[data-ga]') : null;
    if (target && host.contains(target)) {
      paexStorePagePush(enabled, target.dataset.ga, handle);
    }
  }, { signal: signal, capture: true });
}

/* ---------------- hero ---------------- */

if (!customElements.get('paex-store-hero')) {
  customElements.define(
    'paex-store-hero',
    class PaexStoreHero extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        var dataEl = this.querySelector('[data-paex-store-data]');
        try {
          this.data = JSON.parse(dataEl ? dataEl.textContent : '{}');
        } catch (error) {
          this.data = {};
        }
        this.texts = this.data.texts || {};
        paexStorePageBindGa(this, Boolean(this.data.analytics), this.data.handle, this.abortController.signal);

        this.bindHoursToggle();
        this.bindMap();
        this.bindSticky();
        this.bindNotify();

        if (this.data.state === 'normal') {
          this.refreshStatus();
          // re-evaluate each minute so the pill flips at open/close boundaries
          this.statusTimer = setInterval(this.refreshStatus.bind(this), 60000);
        }
      }

      disconnectedCallback() {
        this.abortController.abort();
        if (this.statusTimer) clearInterval(this.statusTimer);
      }

      format(template, values) {
        return paexDeliveryFormat(template, values);
      }

      /* ---------- pill + hours card status ---------- */

      refreshStatus() {
        var store = this.data.store || {};
        if (!store.hours) return;
        var texts = this.texts;
        var info = paexStoreHoursInfo(store, null, {
          timeZone: PAEX_STORE_TZ,
          holidays: this.data.holidays || [],
        });
        if (!info) return;

        var nowParts = paexStoreNowParts(new Date(), PAEX_STORE_TZ);
        var todaySlot = store.hours[String(nowParts.weekday)];
        var todayOpen = todaySlot ? paexStoreParseMinutes(todaySlot.open) : null;
        var todayClose = todaySlot ? paexStoreParseMinutes(todaySlot.close) : null;

        var isOpen = info.state === 'open' || info.state === 'closes_soon';
        this.setAttribute('data-open-state', isOpen ? 'open' : 'closed');

        var word = isOpen ? texts.open : texts.closed;
        var details = [];
        if (isOpen && todayClose != null && texts.closesAt) {
          // design: the pill's closing time keeps its minutes ("9:00 PM")
          details.push(this.format(texts.closesAt, { close: paexStorePageFormatMinutes(todayClose, true) }));
        }
        if (!isOpen && info.nextOpenHour != null) {
          var nextOpenLabel = paexStorePageFormatMinutes(paexStoreParseMinutes(info.nextOpenHour));
          if (info.nextOpenIsToday && texts.opensAt) {
            details.push(this.format(texts.opensAt, { open: nextOpenLabel }));
          } else if (info.nextOpenIsTomorrow && texts.opensTomorrow) {
            details.push(this.format(texts.opensTomorrow, { open: nextOpenLabel }));
          } else if (texts.opensDay && texts.days) {
            details.push(this.format(texts.opensDay, {
              day: texts.days[info.nextOpenWeekday] || '',
              open: nextOpenLabel,
            }));
          }
        }
        var todayText = '';
        if (todayOpen != null && todayClose != null && texts.today) {
          todayText = this.format(texts.today, { hours: paexStorePageRange(todayOpen, todayClose) });
        } else if (texts.closedDay) {
          todayText = texts.closedDay;
        }
        if (todayText && !(info.holidayToday && info.holidayToday.status === 'closed')) {
          details.push(todayText);
        }

        // holiday override — today beats tomorrow; closed beats everything
        var note = '';
        var noteTone = 'alert';
        var holidayToday = info.holidayToday;
        var holidayTomorrow = info.holidayTomorrow;
        if (holidayToday && holidayToday.status === 'closed') {
          word = texts.closed;
          this.setAttribute('data-open-state', 'closed');
          details = [];
          note = this.format(texts.holidayClosed, { name: holidayToday.name || '' });
          if (info.nextOpenHour != null && texts.holidayReopens && texts.days) {
            var reopenLabel = paexStorePageFormatMinutes(paexStoreParseMinutes(info.nextOpenHour));
            details.push(this.format(texts.holidayReopens, {
              day: texts.days[info.nextOpenWeekday] || '',
              open: reopenLabel,
            }));
          }
          todayText = texts.closedDay || '';
        } else if (holidayToday && holidayToday.status === 'special_hours' && holidayToday.special) {
          note = holidayToday.special;
          noteTone = 'special';
        } else if (holidayToday && holidayToday.status === 'regular_hours') {
          note = this.format(texts.holidayRegular, { name: holidayToday.name || '' });
          noteTone = 'regular';
        } else if (holidayTomorrow && holidayTomorrow.status === 'closed') {
          note = this.format(texts.holidayClosedTomorrow, { name: holidayTomorrow.name || '' });
          noteTone = 'alert';
        }

        this.renderPill(word, details.join(' · '));
        this.renderHolidayNote(note, noteTone);
        this.renderCardStatus(word, todayText);
        this.refreshRows(nowParts.weekday);
      }

      renderPill(word, detail) {
        var pill = this.querySelector('[data-store-pill]');
        if (!pill) return;
        var dot = pill.querySelector('[data-pill-dot]');
        var wordEl = pill.querySelector('[data-pill-word]');
        var detailEl = pill.querySelector('[data-pill-detail]');
        if (dot) dot.hidden = false;
        if (wordEl && wordEl.textContent !== (word || '')) wordEl.textContent = word || '';
        var detailText = detail ? '· ' + detail : '';
        if (detailEl && detailEl.textContent !== detailText) detailEl.textContent = detailText;
      }

      renderHolidayNote(note, tone) {
        var el = this.querySelector('[data-holiday-note]');
        if (!el) return;
        el.hidden = !note;
        el.setAttribute('data-tone', tone || 'alert');
        if (el.textContent !== (note || '')) el.textContent = note || '';
      }

      renderCardStatus(word, todayText) {
        var dot = this.querySelector('[data-card-dot]');
        var wordEl = this.querySelector('[data-card-word]');
        var todayEl = this.querySelector('[data-card-today]');
        if (dot) dot.hidden = false;
        if (wordEl && wordEl.textContent !== (word || '')) wordEl.textContent = word || '';
        if (todayEl && todayEl.textContent !== (todayText || '')) todayEl.textContent = todayText || '';
      }

      // Server rows were rotated with the CDN's idea of "today" — verify with
      // the real America/Toronto weekday, re-rotate if stale, and upgrade the
      // 24h ranges to locale wording (EN "10 AM – 9 PM", FR "10 h à 21 h").
      refreshRows(todayWeekday) {
        var table = this.querySelector('[data-hours-table]');
        if (!table) return;
        var rows = Array.prototype.slice.call(table.querySelectorAll('[data-hours-row]'));
        if (!rows.length) return;

        rows.forEach(function (row) {
          var openMin = paexStoreParseMinutes(row.dataset.open);
          var closeMin = paexStoreParseMinutes(row.dataset.close);
          var range = row.querySelector('[data-hours-range]');
          if (range && openMin != null && closeMin != null) {
            var text = paexStorePageRange(openMin, closeMin);
            if (range.textContent !== text) range.textContent = text;
          }
        });

        if (Number(rows[0].dataset.day) !== todayWeekday) {
          var sorted = rows.slice().sort(function (a, b) {
            var da = (Number(a.dataset.day) - todayWeekday + 7) % 7;
            var db = (Number(b.dataset.day) - todayWeekday + 7) % 7;
            return da - db;
          });
          sorted.forEach(function (row) { table.appendChild(row); });
          rows = sorted;
        }
        rows.forEach(function (row, index) {
          row.classList.toggle('paex-store-hero__hours-row--today', index === 0);
        });
      }

      /* ---------- hours card expand (CSS-only collapse) ---------- */

      bindHoursToggle() {
        var card = this.querySelector('[data-hours-card]');
        var toggle = this.querySelector('[data-hours-toggle]');
        if (!card || !toggle) return;
        var openLabel = this.querySelector('[data-toggle-open-label]');
        var closeLabel = this.querySelector('[data-toggle-close-label]');
        var expandTracked = false;
        var self = this;
        toggle.addEventListener('click', function () {
          var expanded = card.hasAttribute('data-expanded');
          if (expanded) card.removeAttribute('data-expanded');
          else card.setAttribute('data-expanded', '');
          toggle.setAttribute('aria-expanded', String(!expanded));
          if (openLabel) openLabel.hidden = !expanded;
          if (closeLabel) closeLabel.hidden = expanded;
          if (!expanded && !expandTracked) {
            expandTracked = true; // first expand only — the useful diagnostic
            paexStorePagePush(Boolean(self.data.analytics), 'hours_expand', self.data.handle);
          }
        }, { signal: this.abortController.signal });
      }

      /* ---------- map: static first, interactive on tap ---------- */

      bindMap() {
        var map = this.querySelector('[data-store-map]');
        var tap = this.querySelector('[data-map-tap]');
        if (!map || !tap) return;
        var self = this;
        var store = this.data.store || {};
        var mbLat = parseFloat(String(store.lat || '').replace(',', '.'));
        var mbLng = parseFloat(String(store.lng || '').replace(',', '.'));
        var mbValid = isFinite(mbLat) && isFinite(mbLng) && Math.abs(mbLat) <= 90 && Math.abs(mbLng) <= 180 && (mbLat !== 0 || mbLng !== 0);
        var mapboxToken = this.dataset.mapboxToken;

        // Sasha 2026-07-31: with a Mapbox token the LIVE map with the store
        // pin shows on page load (design intent) — initialized when the map
        // scrolls into view, so below-the-fold mobile visits still cost
        // nothing until the map is actually seen.
        if (mapboxToken && mbValid && 'IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                io.disconnect();
                self.activateMapbox(map, mapboxToken, self.dataset.mapboxStyle, mbLat, mbLng);
                return;
              }
            }
          }, { rootMargin: '200px' });
          tap.hidden = true; // no "View map" chip — the live map IS the surface
          io.observe(map);
          return;
        }

        tap.addEventListener('click', function () {
          var apiKey = self.dataset.mapApiKey;
          if (mapboxToken && mbValid) {
            return self.activateMapbox(map, mapboxToken, self.dataset.mapboxStyle, mbLat, mbLng);
          }
          if (apiKey && Number.isFinite(Number(store.lat)) && Number.isFinite(Number(store.lng))) {
            self.activateMap(map, apiKey, Number(store.lat), Number(store.lng));
          } else if (tap.dataset.mapsUrl) {
            // no API key: the tap still gets the shopper to a live map —
            // google_maps_url verbatim keeps the GBP/UTM attribution
            window.open(tap.dataset.mapsUrl, '_blank', 'noopener');
          }
        }, { signal: this.abortController.signal });
      }

      // Mapbox GL (Sasha 2026-07-31): preferred engine — one billable map
      // load per TAP, so 15 store pages stay deep inside the 50k/month
      // free tier. CSS+JS injected on demand; nothing loads pre-tap.
      activateMapbox(mapEl, token, styleUrl, lat, lng) {
        var canvas = mapEl.querySelector('[data-map-canvas]');
        if (!canvas) return;
        mapEl.setAttribute('data-map-active', '');
        // a11y (W3.8 low, 2026-08-01): the facade ships aria-hidden (static
        // preview, the tap button carries the action) — but once a live map
        // with focusable controls mounts inside, aria-hidden would hide
        // interactive content from AT (axe aria-hidden-focus). Expose it;
        // the error paths re-hide with the static layer.
        mapEl.removeAttribute('aria-hidden');
        canvas.hidden = false;
        var self = this;
        var boot = function () {
          try {
            window.mapboxgl.accessToken = token;
            var liveMap = new window.mapboxgl.Map({
              container: canvas,
              style: styleUrl || 'mapbox://styles/mapbox/light-v11',
              center: [lng, lat],
              zoom: 15.5, // framed on the store block, not the city
              attributionControl: true,
              cooperativeGestures: true,
              dragRotate: false,
              pitchWithRotate: false,
            });
            liveMap.touchZoomRotate.disableRotation();
            // belt-and-suspenders: re-center once tiles settle so the pin
            // is always the frame's anchor
            liveMap.on('load', function () { liveMap.jumpTo({ center: [lng, lat], zoom: 15.5 }); });
            liveMap.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            new window.mapboxgl.Marker({ color: '#ba2519' }).setLngLat([lng, lat]).addTo(liveMap);
            paexStorePagePush(Boolean(self.data.analytics), 'map_activated', self.data.handle);
          } catch (error) {
            mapEl.removeAttribute('data-map-active');
            mapEl.setAttribute('aria-hidden', 'true');
            canvas.hidden = true;
          }
        };
        if (window.mapboxgl) return boot();
        var css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.css';
        document.head.appendChild(css);
        var script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.js';
        script.onload = boot;
        script.onerror = function () {
          mapEl.removeAttribute('data-map-active');
          mapEl.setAttribute('aria-hidden', 'true');
          canvas.hidden = true;
        };
        document.head.appendChild(script);
      }

      // ss-map loader pattern: the official async bootstrap, injected only on
      // first tap (the Maps JS API never rides on page load).
      activateMap(mapEl, apiKey, lat, lng) {
        var canvas = mapEl.querySelector('[data-map-canvas]');
        if (!canvas) return;
        mapEl.setAttribute('data-map-active', '');
        // a11y (W3.8 low): live map replaces the aria-hidden facade — expose it
        mapEl.removeAttribute('aria-hidden');
        canvas.hidden = false;
        if (!window.google || !window.google.maps || !window.google.maps.importLibrary) {
          /* eslint-disable */
          (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({ key: apiKey, v: 'weekly' });
          /* eslint-enable */
        }
        google.maps.importLibrary('maps').then(function (lib) {
          var liveMap = new lib.Map(canvas, {
            center: { lat: lat, lng: lng },
            zoom: 15,
            // POI labels off — the pin is the only point that matters here
            styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
            disableDefaultUI: false,
          });
          new google.maps.Marker({ map: liveMap, position: { lat: lat, lng: lng } });
        }).catch(function () {
          // loader failed — put the static layer back
          mapEl.removeAttribute('data-map-active');
          mapEl.setAttribute('aria-hidden', 'true');
          canvas.hidden = true;
        });
      }

      // coming-soon "Notify me" scrolls to the page's newsletter section
      // (pe-newsletter is reused as-is, so it carries no anchor of its own)
      bindNotify() {
        var notify = this.querySelector('a[href="#paex-store-newsletter"]');
        if (!notify) return;
        notify.addEventListener('click', function (event) {
          var newsletter = document.querySelector('.paex-nl');
          if (newsletter) {
            event.preventDefault();
            newsletter.scrollIntoView({ behavior: 'smooth', block: 'center' });
            var input = newsletter.querySelector('input[type="email"]');
            if (input) input.focus({ preventScroll: true });
          }
        }, { signal: this.abortController.signal });
      }

      /* ---------- sticky mobile bar ---------- */

      bindSticky() {
        var bar = this.querySelector('[data-store-sticky]');
        var ctas = this.querySelector('[data-hero-ctas]');
        if (!bar || !ctas || !('IntersectionObserver' in window)) return;
        var links = bar.querySelectorAll('a');
        this.stickyObserver = new IntersectionObserver(function (entries) {
          var visible = entries[0] && !entries[0].isIntersecting;
          if (visible) bar.setAttribute('data-visible', '');
          else bar.removeAttribute('data-visible');
          bar.setAttribute('aria-hidden', String(!visible));
          links.forEach(function (link) {
            if (visible) link.removeAttribute('tabindex');
            else link.setAttribute('tabindex', '-1');
          });
        });
        this.stickyObserver.observe(ctas);
        var self = this;
        this.abortController.signal.addEventListener('abort', function () {
          if (self.stickyObserver) self.stickyObserver.disconnect();
        });
      }
    }
  );
}

/* ---------------- event ---------------- */

// Upgrade the event band's server-rendered 24h times ("10:30 – 12:00") to
// locale wording ("10:30 AM – 12:00 PM"), same formatter as the hero hours
// card, minutes kept per the design. Deferred script — the DOM is parsed
// when this runs. Guarded: several sections emit this tag.
if (!window.paexStoreEventTimesDone) {
  window.paexStoreEventTimesDone = true;
  Array.prototype.forEach.call(document.querySelectorAll('[data-event-time]'), function (el) {
    var startMin = paexStoreParseMinutes(el.dataset.start);
    var endMin = paexStoreParseMinutes(el.dataset.end);
    var text = '';
    if (startMin != null && endMin != null) {
      text = paexStorePageFormatMinutes(startMin, true) + paexStorePageRangeJoiner() + paexStorePageFormatMinutes(endMin, true);
    } else if (startMin != null) {
      text = paexStorePageFormatMinutes(startMin, true);
    }
    if (text && el.textContent !== text) el.textContent = text;
  });
}

/* ---------------- nearby ---------------- */

if (!customElements.get('paex-store-nearby')) {
  customElements.define(
    'paex-store-nearby',
    class PaexStoreNearby extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        paexStorePageBindGa(this, this.dataset.analytics === 'true', this.dataset.handle, this.abortController.signal);
        this.refresh();
      }

      disconnectedCallback() {
        this.abortController.abort();
      }

      origin() {
        // user geo when the site-wide loader has cached a real fix,
        // otherwise the current store's own coordinates
        var geo = typeof paexDeliveryReadGeo === 'function' ? paexDeliveryReadGeo() : null;
        if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
          return { lat: geo.lat, lng: geo.lng };
        }
        var lat = Number(this.dataset.originLat);
        var lng = Number(this.dataset.originLng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat: lat, lng: lng };
        return null;
      }

      refresh() {
        var origin = this.origin();
        var limit = Number(this.dataset.limit) > 0 ? Number(this.dataset.limit) : 3;
        var openText = this.dataset.openText || '';
        var closedText = this.dataset.closedText || '';
        var kmText = this.dataset.kmText || '';
        var cards = Array.prototype.slice.call(this.querySelectorAll('[data-nearby-card]'));

        cards.forEach(function (card) {
          var lat = Number(card.dataset.lat);
          var lng = Number(card.dataset.lng);
          var km = null;
          if (origin && Number.isFinite(lat) && Number.isFinite(lng)) {
            km = paexDeliveryDistanceKm(origin.lat, origin.lng, lat, lng);
          }
          card.dataset.km = km == null ? '' : String(km);

          var parts = [];
          if (km != null && kmText) {
            parts.push(paexDeliveryFormat(kmText, { km: km.toFixed(1) }));
          }
          var hours = null;
          try {
            hours = card.dataset.hours ? JSON.parse(card.dataset.hours) : null;
          } catch (error) {
            hours = null;
          }
          if (hours) {
            var info = paexStoreHoursInfo({ hours: hours }, null, { timeZone: PAEX_STORE_TZ });
            if (info) {
              if ((info.state === 'open' || info.state === 'closes_soon') && info.closeHour != null && openText) {
                parts.push(paexDeliveryFormat(openText, {
                  close: paexStorePageFormatMinutes(paexStoreParseMinutes(info.closeHour)),
                }));
              } else if (info.state === 'closed' && info.nextOpenHour != null && closedText) {
                parts.push(paexDeliveryFormat(closedText, {
                  open: paexStorePageFormatMinutes(paexStoreParseMinutes(info.nextOpenHour)),
                }));
              }
            }
          }
          var meta = card.querySelector('[data-nearby-meta]');
          var text = parts.join(' · ');
          if (meta && meta.textContent !== text) meta.textContent = text;
        });

        if (origin) {
          var list = this.querySelector('[data-nearby-list]');
          cards
            .slice()
            .sort(function (a, b) {
              var da = a.dataset.km === '' ? Infinity : Number(a.dataset.km);
              var db = b.dataset.km === '' ? Infinity : Number(b.dataset.km);
              return da - db;
            })
            .forEach(function (card, index) {
              if (list) list.appendChild(card);
              card.hidden = index >= limit;
            });
        } else {
          cards.forEach(function (card, index) {
            card.hidden = index >= limit;
          });
        }
      }
    }
  );
}
